import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
  Paper,
  Fade,
  Grow,
  useMediaQuery,
  AppBar,
  IconButton,
  Toolbar,
} from "@mui/material";
import { motion } from "framer-motion";
import { ColorModeContext, tokens } from "../../theme";
import { useBranding } from "../../BrandingContext";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";

const sections = [
  { id: "intro", label: "Introduction" },
  { id: "collect", label: "Information We Collect" },
  { id: "use", label: "How We Use Your Information" },
  { id: "share", label: "Sharing Your Information" },
  { id: "security", label: "Data Security" },
  { id: "choices", label: "Your Privacy Choices" },
  { id: "cookies", label: "Cookies and Tracking" },
  { id: "children", label: "Children’s Privacy" },
  { id: "links", label: "Third-Party Links" },
  { id: "transfers", label: "International Data Transfers" },
  { id: "updates", label: "Updates to This Policy" },
];

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const colorMode = React.useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [activeSection, setActiveSection] = useState("intro");
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const logoSrc =
    theme.palette.mode === "dark"
      ? branding?.logoWhite ||
        require("../../branding-assets/v3biomed/white-logo.png")
      : branding?.logo || require("../../branding-assets/v3biomed/logo.png");

  const scrollToSection = (id: string) => {
    const section = sectionRefs.current[id];
    if (section) {
      const yOffset = -100;
      const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setHighlighted(id);
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const offset = 120;
      let current = "intro";

      for (const { id } of sections) {
        const ref = sectionRefs.current[id];
        if (ref) {
          const sectionTop = ref.offsetTop - offset;
          if (scrollPosition >= sectionTop) {
            current = id;
          }
        }
      }
      setActiveSection(current);
      setHighlighted(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        pt: 12,
        pb: 10,
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      }}
    >
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
              : "linear-gradient(to right, #e0f7fa, #ffffff)",
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
          <Box display="flex" alignItems="center">
            <img
              src={logoSrc}
              alt="V3Biomed Logo"
              style={{ height: 40, objectFit: "contain" }}
            />
          </Box>
          <IconButton onClick={colorMode.toggleColorMode} color="inherit">
            {theme.palette.mode === "dark" ? (
              <LightModeOutlined />
            ) : (
              <DarkModeOutlined />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Grid container spacing={4} px={{ xs: 2, md: 8 }}>
        {!isMobile && (
          <Grid item md={3}>
            <Fade in timeout={700}>
              <Box
                sx={{
                  position: "sticky",
                  top: 100,
                  p: 3,
                  borderRadius: 4,
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
                      : "linear-gradient(to right, #e0f7fa, #ffffff)",
                  border: "2px solid rgba(110, 110, 110, 0.3)",
                  boxShadow: 3,
                }}
              >
                <List>
                  {sections.map((section) => {
                    const isSelected = activeSection === section.id;
                    return (
                      <ListItemButton
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        selected={isSelected}
                        sx={{
                          borderRadius: 2,
                          transition: "all 0.3s",
                          mb: 1,
                          color: isSelected
                            ? colors.success[300]
                            : colors.grey[300],
                          fontWeight: 600,
                          "&:hover": {
                            pl: 2,
                            bgcolor: colors.grey[700],
                            color: colors.grey[100],
                          },
                          "&.Mui-selected": {
                            bgcolor: colors.success[500],
                            color: colors.success[100],
                            fontWeight: 600,
                          },
                        }}
                      >
                        <ListItemText
                          primary={section.label}
                          primaryTypographyProps={{
                            fontWeight: 600,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            </Fade>
          </Grid>
        )}

        <Grid item xs={12} md={9}>
          <Grow in timeout={900}>
            <Paper
              elevation={6}
              sx={{
                borderRadius: 4,
                p: { xs: 3, md: 6 },
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
                    : "linear-gradient(to right, #e0f7fa, #ffffff)",
                border: "2px solid rgba(110, 110, 110, 0.3)",
                backdropFilter: "blur(12px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 0 30px rgba(0,0,0,0.4)"
                    : "0 0 24px rgba(0,0,0,0.1)",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={900}
                  align="center"
                  gutterBottom
                  sx={{ fontSize: { xs: "2rem", md: "2.75rem" } }}
                >
                  Privacy Policy
                </Typography>
                <Typography variant="subtitle2" align="center" gutterBottom>
                  <strong>Effective Date:</strong> July 1st, 2024
                </Typography>
                <Divider sx={{ my: 4 }} />

                {sections.map(({ id, label }) => (
                  <motion.div
                    key={id}
                    ref={(el) => (sectionRefs.current[id] = el)}
                    id={id}
                    style={{ scrollMarginTop: 120 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                    <motion.div
                      initial={{ backgroundColor: "transparent" }}
                      animate={{
                        backgroundColor:
                          highlighted === id
                            ? theme.palette.mode === "dark"
                              ? "rgba(100, 255, 218, 0.05)"
                              : "rgba(255, 255, 0, 0.15)"
                            : "transparent",
                      }}
                      transition={{ duration: 1 }}
                      style={{ borderRadius: 12, padding: 16 }}
                    >
                      <Typography variant="h4" fontWeight={800} gutterBottom>
                        {label}
                      </Typography>
                      <Typography
                        variant="h6"
                        paragraph
                        color={colors.grey[400]}
                      >
                        {getSectionContent(id,branding)}
                      </Typography>
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            </Paper>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};

// Helper to return section content
const getSectionContent = (id: string ,branding:any): React.ReactNode => {
  switch (id) {
    case "intro":
      return (
        <>
          Welcome to {branding?.companyName || "V3Biomed"}. We are committed to protecting your privacy and
          ensuring that your personal and business information is handled
          securely and responsibly. This Privacy Policy outlines how {branding?.companyName || "V3Biomed"} 
           (“we,” “our,” or “us”) collects, uses, and safeguards information
          through our websites, public portal (
            <a
            href={branding?.brandurl?.live || "https://v3biomedical.com/"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4fc3f7",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {branding?.brandurl?.name ||"v3biomedical.com"}
          </a>
          ), and ordering
          portal (
          <a
            href={branding?.brandurl?.live || "https://v3biomedical.com/"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4fc3f7",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {branding?.brandurl?.name ||"v3biomedical.com"}
          </a>
          ).
        </>
      );

    case "collect":
      return (
        <>
          <strong>a. Personal Information</strong>
          <br />
          This includes data that identifies you as an individual, such as:
          <ul>
            <li>
              Name, email address, phone number, and business details (for
              healthcare providers and manufacturers).
            </li>
            <li>
              Patient information, including name, date of birth, medical
              history, and insurance details, as required for insurance
              verification (collected in compliance with applicable healthcare
              privacy laws).
            </li>
          </ul>
          <strong>b. Non-Personal Information</strong>
          <br />
          This includes aggregated data that does not identify individuals, such
          as:
          <ul>
            <li>Browser type, operating system, and device information.</li>
            <li>IP address and geolocation data.</li>
            <li>
              Website usage data, such as pages visited and time spent on the
              website.
            </li>
          </ul>
          <strong>c. Transactional Data</strong>
          <br />
          Information related to the placement and fulfillment of wound care
          product orders.
        </>
      );

    case "use":
      return (
        <>
          We use the information collected for the following purposes:
          <ul>
            <li>
              <strong>Service Delivery:</strong> To facilitate connections
              between wound care providers and manufacturers, verify patient
              insurance, and process orders.
            </li>
            <li>
              <strong>Customer Support:</strong> To respond to inquiries,
              provide technical assistance, and manage user accounts.
            </li>
            <li>
              <strong>Compliance:</strong> To comply with applicable healthcare
              laws, insurance requirements, and contractual obligations.
            </li>
            <li>
              <strong>Website Operations:</strong> To improve our websites,
              enhance user experience, and monitor for security threats.
            </li>
          </ul>
        </>
      );

    case "share":
      return (
        <>
          We share your information only in the following circumstances:
          <ul>
            <li>
              <strong>With Healthcare Providers and Manufacturers:</strong> To
              facilitate product placement and communication.
            </li>
            <li>
              <strong>With Insurance Providers:</strong> For patient insurance
              verification purposes.
            </li>
            <li>
              <strong>With Service Providers:</strong> Trusted third-party
              vendors who perform services on our behalf (e.g., IT services,
              payment processors), bound by confidentiality agreements.
            </li>
            <li>
              <strong>For Legal Reasons:</strong> When required by law,
              regulation, or legal process.
            </li>
          </ul>
        </>
      );

    case "security":
      return (
        <>
          We implement industry-standard measures to protect your information,
          including:
          <ul>
            <li>
              Encryption of sensitive data during transmission (e.g., patient
              data and insurance information).
            </li>
            <li>
              Secure servers and firewalls to prevent unauthorized access.
            </li>
            <li>
              Regular audits and staff training on data privacy and security
              protocols.
            </li>
          </ul>
        </>
      );

    case "choices":
      return (
        <>
          You have the following rights regarding your information:
          <ul>
            <li>
              <strong>Access and Correction:</strong> Request access to and
              correction of your personal information.
            </li>
            <li>
              <strong>Data Deletion: </strong> Request deletion of your
              information, subject to legal and contractual obligations.
            </li>
            <li>
              <strong>Opt-Out:</strong> Choose not to receive marketing
              communications by following the instructions in our emails or
              contacting us directly.
            </li>
          </ul>
        </>
      );

    case "cookies":
      return `We use cookies and similar technologies to enhance website functionality and analyze usage patterns. You can control cookie preferences through your browser settings.`;

    case "children":
      return `Our services are not directed at individuals under the age of 18. We do not knowingly collect personal information from children without parental consent.`;

    case "links":
      return `Our websites may include links to third-party sites. ${branding?.companyName || "V3Biomed"} is not responsible for the privacy practices of these external sites, and we encourage you to review their privacy policies.`;

    case "transfers":
      return `If you access our services from outside the United States, please note that your data may be transferred and processed in the United States or other countries with different data protection laws.`;

    case "updates":
      return `We may update this Privacy Policy from time to time. Changes will be posted on this page with the updated effective date.`;

    default:
      return null;
  }
};

export default PrivacyPolicy;
